// hooks/useProfileController.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';

export default function useProfileController() {
  const { session } = useSession();
  const [profile, setProfile] = useState({
    fullname: '',
    shortname: '',
    country: '',
    city: '',
    phone: '',
    email: '',
    linkedin: '',
    github: '',
    behance: '',
    instagram: '',
    youtube: '',
    personal_web: '',
    user_id: session?.user?.id || '',
  });
  const [avatarUrl, setAvatarUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchProfile = async () => {
      try {
        setLoading(true);

        const { data: profileData, error: profileError } = await supabase
          .from('profile')
          .select('*')
          .eq('user_id', session?.user?.id)
          .single();

        if (isMounted) {
          if (profileError) {
            if (profileError.code === 'PGRST116') {
              console.log('No existing profile found. Please fill in your details.');
            } else {
              throw profileError;
            }
          } else if (profileData) {
            setProfile(profileData);

            const { data: imageData, error: imageError } = await supabase
              .from('image')
              .select('*')
              .eq('user_id', profileData.user_id)
              .eq('category', 'avatar')
              .order('created_at', { ascending: false })
              .limit(1)
              .single();

            if (imageError && imageError.code !== 'PGRST116') {
              console.error('Error fetching image:', imageError);
            } else if (imageData) {
              const { data: publicURLData } = supabase
                .storage
                .from('document')
                .getPublicUrl(imageData.path);

              if (publicURLData) {
                setAvatarUrl(publicURLData.publicUrl);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to fetch profile');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (session) {
      fetchProfile();
    }

    return () => {
      isMounted = false;
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [session]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      let newAvatarUrl = avatarUrl;
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${fileName}`;

        let { error: uploadError } = await supabase.storage
          .from('document')
          .upload(filePath, selectedFile);

        if (uploadError) throw uploadError;

        const { error: imageError } = await supabase
          .from('image')
          .insert({
            user_id: session.user.id,
            category: 'avatar',
            path: filePath,
          });

        if (imageError) throw imageError;

        const { data: publicURLData } = supabase
          .storage
          .from('document')
          .getPublicUrl(filePath);
        
        newAvatarUrl = publicURLData.publicUrl;
        setAvatarUrl(newAvatarUrl);
      }

      const profileToUpdate = {
        ...profile,
        user_id: session?.user?.id || profile.user_id,
      };

      const { data, error } = await supabase
        .from('profile')
        .upsert(profileToUpdate)
        .select()
        .single();

      if (error) throw error;

      setProfile(data);
      setSelectedFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    try {
      if (!e.target.files || e.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = e.target.files[0];
      
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      
      const url = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewUrl(url);
    } catch (error) {
      alert(error.message);
    }
  };

  return {
    profile,
    avatarUrl,
    previewUrl,
    loading,
    error,
    updateProfile,
    handleChange,
    handleFileUpload,
  };
}